<template>
  <v-card>
    <v-card-title>
      {{ label || resourceName }}
      <v-btn
        @click="openCreateForm()"
        v-if="allowNew && canEdit"
        class="ml-2"
        icon
        color="primary"
        small
        elevation="3"
      >
        <v-icon>add</v-icon>
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn icon @click="getResources" v-if="canView"
        ><v-icon>refresh</v-icon></v-btn
      >
    </v-card-title>
    <v-card-text v-if="canView">
      <v-text-field
        v-model="searchText"
        single-line
        append-icon="mdi-magnify"
        placeholder="Search..."
      ></v-text-field>

      <v-data-table
        v-bind="$attrs"
        :items="resources"
        :headers="_columns"
        @click:row="selectItem"
        :loading="loading"
        :search="searchText"
        dense
      >
        <template v-slot:item="{ item }">
          <slot name="item" :item="{ item }">
            <tr @click="selectItem(item)">
              <td v-for="column of _columns" :key="`item-${column.value}`">
                <slot :name="`item.${column.value}`" :item="item">
                  {{ item[column.value] }}
                </slot>
              </td>
            </tr>
          </slot>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text v-else>
      <v-alert type="information">
        You don't have the '{{ editPermission }}' permission. Please contact
        your administrator if you need access
      </v-alert>
    </v-card-text>
    <v-dialog v-model="displayDetail" max-width="40em">
      <v-card>
        <v-card-title>
          {{ selectedItem[resourceIdName] ? "Edit" : "Add" }}
          {{ label || resourceName }}
        </v-card-title>
        <v-form card @submit.prevent="saveResource" :disabled="!canEdit">
          <v-card-text>
            <slot name="form" :item="selectedItem" v-if="selectedItem">
              <v-text-field
                v-for="key of fieldsToEdit"
                :key="`item-${key}`"
                :label="key"
                v-model="selectedItem[key]"
              ></v-text-field>
            </slot>
          </v-card-text>
          <v-card-actions>
            <v-btn
              :disabled="!canEdit"
              @click="displayDeleteForm = true"
              v-if="allowDelete && selectedItem[resourceIdName]"
              color="error"
              >Delete...</v-btn
            >
            <v-alert v-if="!canEdit" type="info" dense size="small">
              You don't have the '{{ editPermission }}' permission. Please
              contact your administrator if you need to make changes
            </v-alert>
            <v-spacer></v-spacer>
            <v-alert type="error" v-if="saveError" size="small" dense>{{
              saveError.message
            }}</v-alert>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              type="submit"
              :loading="saving"
              v-if="canEdit && allowEdit"
            >
              Save <v-icon v-if="saved">check</v-icon>
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog card v-model="displayDeleteForm" max-width="20em">
      <v-card>
        <v-card-title>Are you sure?</v-card-title>
        <v-card-text>
          Are you sure you want to delete this? (This action cannot be undone)
          <v-alert v-if="deleteError">{{ deleteError.message }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="displayDeleteForm = false" color="primary">
            No, keep it
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn @click="deleteResource" :loading="deleting" color="error">
            Yes, delete it
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: "RestResourceTable",
  props: {
    label: String,
    resourceName: String,
    resourceIdName: String,
    allowNew: Boolean,
    allowEdit: { type: Boolean, default: true, required: false },
    allowDelete: Boolean,
    columns: Array,
    filter: Object,
    getItems: Function,
    viewPermission: String,
    editPermission: String
  },
  data() {
    return {
      resources: [],
      loading: false,
      saving: false,
      saved: false,
      error: null,
      selectedItem: {},
      displayDetail: false,
      displayDeleteForm: false,
      saveError: null,
      searchText: "",
      displayNewForm: false,
      deleting: false,
      deleteError: null
    };
  },
  computed: {
    fieldsToEdit() {
      const fields = [];
      try {
        fields = Object.keys(this.resources[0]).filter(f => f !== "@type");
      } finally {
        return fields;
      }
    },
    _columns() {
      let cols = this.columns;
      if (
        typeof cols === "undefined" &&
        this.resources.length &&
        this.resources[0][this.resourceIdName]
      ) {
        cols = Object.keys(this.resources[0])
          .filter(f => f !== "@type")
          .map(c => {
            return { text: c, value: c };
          });
      }
      return cols;
    },
    canView() {
      if (!this.viewPermission) {
        return true;
      } else {
        return this.currentUserCan(this.viewPermission);
      }
    },
    canEdit() {
      if (this.canView === false) {
        return false;
      }
      if (!this.editPermission) {
        return true;
      } else {
        return this.currentUserCan(this.editPermission);
      }
    }
  },
  methods: {
    async getResources() {
      var resources = [];
      try {
        this.error = null;
        this.loading = true;
        if (this.getItems && typeof this.getItems === "function") {
          resources = this.getItems();
        } else {
          let { data } = await this.$http.get(`/${this.resourceName}`, {
            params: { filter: this.filter ? this.filter : {} }
          });

          resources = [...data];
        }
        this.resources = [...resources];
      } catch (e) {
        this.error = e;
      } finally {
        this.loading = false;
      }
      return resources;
    },
    async saveResource() {
      if (!this.canEdit) {
        return;
      }
      try {
        this.saveError = null;
        this.saving = true;
        this.saved = false;

        if (this.saveItem) {
          const result = await this.saveItem(this.selectedItem);
          if (result) {
            this.selectedItem = { ...result };
          }
        } else {
          let { data } = await this.$http.patch(
            `/${this.resourceName}`,
            this.selectedItem
          );
          this.selectedItem = { ...data };
        }
        this.$emit("saved", this.selectedItem);
        await this.getResources();
        this.saved = true;
      } catch (e) {
        this.saveError = e;
      } finally {
        this.saving = false;
      }
    },
    async deleteResource() {
      if (!this.canEdit) {
        return;
      }
      const resourceId = this.selectedItem[this.resourceIdName];
      if (!resourceId) {
        return;
      }
      try {
        this.deleting = true;

        await this.$http.delete(`/${this.resourceName}/${resourceId}`);
        this.selectedItem = {};
        this.$emit("saved");
        this.displayDeleteForm = false;
        this.displayDetail = false;
      } catch (e) {
        this.deleteError = e;
      } finally {
        this.deleting = false;
        await this.getResources();
      }
    },
    async openCreateForm() {
      this.selectedItem = {};
      /* eslint-disable-next-line */
      for (let key of this.fieldsToEdit.filter(
        f => f !== this.resourceIdName
      )) {
        this.$set(this.selectedItem, key, "");
      }
      this.displayDetail = true;
    },
    selectItem(item) {
      this.selectedItem = { ...item };
      this.displayDetail = true;
      this.$emit("select", item);
    },
    async refresh() {
      await this.getResources();
      if (this.selectedItem[this.resourceIdName]) {
        const selectedId = this.selectedItem[this.resourceIdName];
        this.selectedItem =
          this.resources.find(r => r[this.resourceIdName] == selectedId) || {};
      }
    }
  },
  created() {
    this.getResources();
  },
  watch: {
    selectedItem: {
      deep: true,
      handler() {
        this.saved = false;
      }
    }
  }
};
</script>
